import { formatNumberLocale } from 'src/locales';
import Decimal from 'big.js';

// ----------------------------------------------------------------------

/*
 * Locales code
 * https://gist.github.com/raushankrjha/d1c7e35cf87e69aa8b4208a8171a8416
 */

export type InputNumberValue = string | number | null | undefined;

type Options = Intl.NumberFormatOptions & { currency?: string };

const DEFAULT_LOCALE = { code: 'en-US', currency: 'EUR' };

function processInput(inputValue: InputNumberValue): number | null {
  if (inputValue == null || Number.isNaN(inputValue)) return null;
  const num = Number(inputValue);
  if (num > -0.005 && num <= 0) return 0;
  return num;
}

// ----------------------------------------------------------------------

export function fNumber(inputValue: InputNumberValue, options?: Options) {
  const locale = formatNumberLocale() || DEFAULT_LOCALE;

  const number = processInput(inputValue);
  if (number === null) return '';

  const fm = new Intl.NumberFormat(locale.code, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  }).format(number);

  return fm;
}

// ----------------------------------------------------------------------

export function fCurrency(inputValue: InputNumberValue, options?: Options) {
  const locale = formatNumberLocale() || DEFAULT_LOCALE;

  const number = processInput(inputValue);

  if (number === null) {
    return '';
  }

  // We try to use EUR/SEK while using english language with locale en-US and we get numbers like "-EUR 500.00".
  // Hardcoded the european standard and we will get "-500.00 USD/EUR/SEK" etc.
  // We have to revisit this if we want "- $500.00"
  const fm = new Intl.NumberFormat('de-DE', {
    // locale.code
    style: 'currency',
    currencyDisplay: 'code',
    currencySign: 'standard',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
    currency: options?.currency || 'EUR',
  }).format(number);

  return fm;
}

// export function formatCurrency(num: number, currency = "EUR"): string {
//   return new Intl.NumberFormat("en-US", {
//     style: "currency",
//     currency,
//     currencyDisplay: "code",
//     currencySign: "accounting",
//   }).format(num);
// }

// ----------------------------------------------------------------------

export function fPercent(inputValue: InputNumberValue, options?: Options) {
  const locale = formatNumberLocale() || DEFAULT_LOCALE;

  const number = processInput(inputValue);
  if (number === null) return '';

  const fm = new Intl.NumberFormat(locale.code, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    ...options,
  }).format(number / 100);

  return fm;
}

// ----------------------------------------------------------------------

export function fShortenNumber(inputValue: InputNumberValue, options?: Options) {
  const locale = formatNumberLocale() || DEFAULT_LOCALE;

  const number = processInput(inputValue);
  if (number === null) return '';

  const fm = new Intl.NumberFormat(locale.code, {
    notation: 'compact',
    maximumFractionDigits: 2,
    ...options,
  }).format(number);

  return fm.replace(/[A-Z]/g, (match) => match.toLowerCase());
}

// ----------------------------------------------------------------------

export function fData(inputValue: InputNumberValue) {
  const number = processInput(inputValue);
  if (number === null || number === 0) return '0 bytes';

  const units = ['bytes', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb'];
  const decimal = 2;
  const baseValue = 1024;

  const index = Math.floor(Math.log(number) / Math.log(baseValue));
  const fm = `${parseFloat((number / baseValue ** index).toFixed(decimal))} ${units[index]}`;

  return fm;
}

export function fAmount(inputValue: InputNumberValue) {
  return Decimal(Number(inputValue || 0)).toFixed(2, Decimal.roundHalfUp);
}
